/**
 * 通用小组件：状态徽标 / 空态 / 加载 / 错误 / 分页 / 头像 / Markdown
 */
import React, { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Image,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  ErrorCircle24Regular,
  Info24Regular,
  Search20Regular,
  Warning24Regular,
} from '@fluentui/react-icons'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { Link } from 'react-router-dom'
import { fmtDateTime, fmtRelative } from './dateTime'
import { BRAND } from '@/theme'

// ==================== 状态徽标 ====================

/** 业务层颜色（含 neutral/info 别名），映射到 Fluent Badge 合法颜色 */
export type BadgeColor = 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'important' | 'severe' | 'subtle'

type FluentBadgeColor = 'brand' | 'success' | 'warning' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle'

const BADGE_COLOR_MAP: Record<BadgeColor, FluentBadgeColor> = {
  brand: 'brand',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'informative',
  neutral: 'subtle',
  important: 'important',
  severe: 'severe',
  subtle: 'subtle',
}

export function toBadgeColor(c: BadgeColor | string): FluentBadgeColor {
  return (BADGE_COLOR_MAP as Record<string, FluentBadgeColor>)[c] ?? 'subtle'
}

const useStyles = makeStyles({
  root: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
})

export function StatusBadge({ color = 'neutral', label, icon }: { color?: BadgeColor; label: string; icon?: React.ReactNode }) {
  const s = useStyles()
  return (
    <Badge appearance="tint" color={toBadgeColor(color)} className={s.root}>
      {icon}
      {label}
    </Badge>
  )
}

// ==================== 空状态 ====================

const useEmptyStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '48px 24px',
    color: tokens.colorNeutralForeground3,
  },
  icon: { fontSize: '48px' },
})

export function EmptyState({ icon, title, description, action }: { icon?: React.ReactNode; title: string; description?: string; action?: React.ReactNode }) {
  const s = useEmptyStyles()
  return (
    <div className={s.root}>
      <div className={s.icon}>{icon ?? <Search20Regular />}</div>
      <Text size={400} weight="semibold">
        {title}
      </Text>
      {description && (
        <Caption1 block align="center">
          {description}
        </Caption1>
      )}
      {action}
    </div>
  )
}

// ==================== 加载 / 错误 ====================

export function LoadingBlock({ label = '加载中…' }: { label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '48px 0' }}>
      <Spinner size="medium" />
      <Text size={300}>{label}</Text>
    </div>
  )
}

export function ErrorBlock({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : '加载失败，请稍后重试'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px 0' }}>
      <DismissCircle24Regular style={{ fontSize: '40px', color: tokens.colorStatusDangerForeground1 }} />
      <Text size={300}>{msg}</Text>
      {onRetry && (
        <Button size="small" appearance="primary" onClick={onRetry}>
          重试
        </Button>
      )}
    </div>
  )
}

// ==================== 分页 ====================

const usePagerStyles = makeStyles({
  root: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '20px' },
  info: { marginRight: '8px', color: tokens.colorNeutralForeground3 },
})

export function Pager({
  page,
  totalPages,
  totalCount,
  onChange,
}: {
  page: number
  totalPages: number
  totalCount?: number
  onChange: (page: number) => void
}) {
  const s = usePagerStyles()
  if (totalPages <= 1) return totalCount ? null : null
  const pages: (number | '…')[] = []
  const start = Math.max(1, page - 2)
  const end = Math.min(totalPages, page + 2)
  if (start > 1) pages.push(1, '…')
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < totalPages) pages.push('…', totalPages)

  return (
    <div className={s.root}>
      {totalCount !== undefined && <span className={s.info}>共 {totalCount} 条</span>}
      <Button size="small" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        上一页
      </Button>
      {pages.map((p, i) =>
        p === '…' ? (
          <Text key={`e${i}`}>…</Text>
        ) : (
          <Button key={p} size="small" appearance={p === page ? 'primary' : 'secondary'} onClick={() => onChange(p)}>
            {p}
          </Button>
        ),
      )}
      <Button size="small" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        下一页
      </Button>
    </div>
  )
}

// ==================== 头像 / Logo ====================

const useImgStyles = makeStyles({
  img: {
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: tokens.colorNeutralBackground3,
  },
})

export function UserAvatar({ src, name, size = 32 }: { src?: string | null; name?: string; size?: number }) {
  const s = useImgStyles()
  const initial = (name || 'U').trim().charAt(0).toUpperCase()
  if (!src) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${BRAND}, #083b77)`,
          color: '#fff',
          fontWeight: 600,
          fontSize: size * 0.45,
          flexShrink: 0,
        }}
      >
        {initial}
      </div>
    )
  }
  return <Image className={s.img} src={src} alt={name || 'avatar'} width={size} height={size} />
}

const useLogoStyles = makeStyles({
  img: {
    borderRadius: '10px',
    objectFit: 'cover',
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
  },
  fallback: {
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    background: `linear-gradient(135deg, ${BRAND}, #083b77)`,
    flexShrink: 0,
  },
})

export function LogoImg({ src, name, size = 48 }: { src?: string | null; name?: string; size?: number }) {
  const s = useLogoStyles()
  const initial = (name || '?').trim().charAt(0).toUpperCase()
  const [failed, setFailed] = React.useState(false)
  if (!src || failed) {
    return (
      <div className={s.fallback} style={{ width: size, height: size, fontSize: size * 0.42 }}>
        {initial}
      </div>
    )
  }
  return <Image className={s.img} src={src} alt={name || ''} width={size} height={size} onError={() => setFailed(true)} />
}

// ==================== Markdown ====================

export function Markdown({ content }: { content?: string | null }) {
  const html = useMemo(() => {
    if (!content) return ''
    const raw = marked.parse(content, { async: false, gfm: true, breaks: true }) as string
    return DOMPurify.sanitize(raw, { ADD_ATTR: ['target'] })
  }, [content])
  return <div className="md" dangerouslySetInnerHTML={{ __html: html }} />
}

// ==================== 页面标题 ====================

const useHeaderStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
})

export function PageHeader({ title, subtitle, actions }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode }) {
  const s = useHeaderStyles()
  return (
    <div className={s.root}>
      <div>
        <Text as="h1" size={700} weight="semibold" style={{ margin: '0px' }}>
          {title}
        </Text>
        {subtitle && (
          <Caption1 block style={{ marginTop: '4px', color: tokens.colorNeutralForeground3 }}>
            {subtitle}
          </Caption1>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>{actions}</div>}
    </div>
  )
}

// ==================== 卡片链接 ====================

const useCardStyles = makeStyles({
  card: {
    display: 'block',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
  },
})

export function CardLink({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) {
  const s = useCardStyles()
  return (
    <Link to={to} className={mergeClasses(s.card, className)}>
      {children}
    </Link>
  )
}

// ==================== 确认对话框 ====================

export function ConfirmDialog({
  open,
  title,
  message,
  danger,
  confirmText = '确认',
  cancelText = '取消',
  loading,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  message?: string
  danger?: boolean
  confirmText?: string
  cancelText?: string
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          {message && <DialogContent>{message}</DialogContent>}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>
            <Button appearance={danger ? 'primary' : 'primary'} onClick={onConfirm} disabled={loading}>
              {loading ? '处理中…' : confirmText}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

// ==================== 消息横幅 ====================

export function InfoBanner({ kind = 'info', title, children }: { kind?: 'info' | 'warning' | 'error' | 'success'; title?: string; children?: React.ReactNode }) {
  const icons = {
    info: <Info24Regular />,
    warning: <Warning24Regular />,
    error: <ErrorCircle24Regular />,
    success: <CheckmarkCircle24Regular />,
  }
  const colors = {
    info: tokens.colorNeutralBackground3,
    warning: tokens.colorPaletteDarkOrangeBackground2,
    error: tokens.colorPaletteRedBackground2,
    success: tokens.colorPaletteGreenBackground2,
  }
  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        padding: '12px 14px',
        borderRadius: '8px',
        background: colors[kind],
        margin: '8px 0',
      }}
    >
      <span style={{ display: 'inline-flex', marginTop: '2px' }}>{icons[kind]}</span>
      <div>
        {title && (
          <Text block weight="semibold" size={300}>
            {title}
          </Text>
        )}
        {children && <div style={{ marginTop: '2px' }}>{children}</div>}
      </div>
    </div>
  )
}

export function TimeText({ value }: { value?: string | null }) {
  return (
    <Tooltip content={fmtDateTime(value)} relationship="label">
      <Text size={200} style={{ color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>
        {fmtRelative(value)}
      </Text>
    </Tooltip>
  )
}
